import { t } from '@lingui/core/macro'
import { X } from '@untitled-ui/icons-react'
import { forwardRef, ReactNode } from 'react'
import {
  Dialog as RCADialog,
  DialogProps as RCADialogProps,
  Heading
} from 'react-aria-components'
import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'
import { Button } from './Button'

export interface DialogProps extends RCADialogProps {
  title?: string
  titleClassName?: string
  description?: string
  descriptionClassName?: string
  buttonCloseLabel?: string
  buttonActionLabel?: string
  customActionButton?: ReactNode
  extraHeader?: ReactNode
  onAction?: () => void
  onCloseWithBtn?: () => void
  contentClassName?: string
  icon?: ReactNode
  iconClassName?: string
  restrictWidth?: boolean | string
  actionButtonDisabled?: boolean
  actionButtonShowLoader?: boolean
  submitButton?: boolean
  onCloseIsXButton?: boolean
  panelClassName?: string
  hideCloseButton?: boolean
}

const dialogStyles = tv({
  base: 'relative outline-none focus:outline-none',
  variants: {
    restrictWidth: {
      true: 'max-w-[400px]'
    }
  }
})

const iconStyles = tv({
  base: 'w-10 shrink-0 h-10 flex rounded-full items-center justify-center',
  variants: {
    isAlert: {
      true: 'bg-critical-100 text-critical-500',
      false: 'bg-brand-100 text-brand-500'
    }
  }
})

const headingStyles = tv({
  base: "text-neutral-900 text-left text-[20px] font-bold font-['Plus Jakarta Sans'] break-normal leading-tight"
})

const Dialog = forwardRef<HTMLDivElement, DialogProps>(
  (
    {
      title,
      titleClassName,
      description,
      descriptionClassName,
      buttonCloseLabel,
      buttonActionLabel,
      children,
      onCloseWithBtn,
      extraHeader,
      icon,
      contentClassName,
      restrictWidth = true,
      iconClassName,
      onCloseIsXButton = false,
      panelClassName,
      hideCloseButton = false,
      ...props
    },
    ref
  ) => {
    return (
      <RCADialog
        {...props}
        className={twMerge(
          dialogStyles({
            restrictWidth:
              typeof restrictWidth === 'boolean' ? restrictWidth : false
          }),
          props.className,
          typeof restrictWidth === 'string' ? ` ${restrictWidth}` : ''
        )}>
        {({ close }) => (
          <div
            ref={ref}
            className={twMerge(
              'mx-2 relative rounded-lg shadow flex min-h-0 justify-start self-stretch max-h-[80vh] bg-neutral-0 my-auto items-start flex-col',
              panelClassName
            )}>
            {/* Header section */}
            <div
              className={twMerge(
                `relative flex flex-row justify-start gap-4 w-full`,
                description ? 'items-start' : 'items-center',
                !!title || !!description || !!extraHeader ? 'p-4 border-b' : ''
              )}>
              {icon && (
                <span
                  className={twMerge(
                    iconStyles({ isAlert: props.role === 'alertdialog' }),
                    iconClassName
                  )}>
                  {icon}
                </span>
              )}
              {!!title || !!description || !!extraHeader ? (
                <div className={'flex flex-col justify-start items-start'}>
                  {!!title && (
                    <Heading
                      className={twMerge(headingStyles(), titleClassName)}
                      slot='title'>
                      {title}
                    </Heading>
                  )}
                  {!!description && (
                    <p
                      className={twMerge(
                        'text-neutral-600 text-sm font-normal text-left',
                        descriptionClassName
                      )}>
                      {description}
                    </p>
                  )}
                  {extraHeader ? extraHeader : null}
                </div>
              ) : null}

              {onCloseIsXButton && (
                <div
                  className={
                    'absolute z-10 top-2 right-2 flex flex-row justify-end items-center'
                  }>
                  <X
                    width={20}
                    className={'cursor-pointer'}
                    onClick={() => {
                      void (!onCloseWithBtn ? close : onCloseWithBtn)()
                    }}
                  />
                </div>
              )}
            </div>

            {/* Content section */}
            <div
              className={twMerge(
                'flex flex-col flex-1 w-full min-h-0 overflow-y-auto scrollbar-hide p-4',
                contentClassName
              )}>
              <>{children}</>
            </div>

            {/* Footer section */}
            {(buttonCloseLabel ||
              !!props.onAction ||
              !!props.customActionButton) && (
              <div className={'flex flex-col shrink-0 w-full p-4'}>
                <div className={`w-full flex items-center gap-2 justify-end `}>
                  {!onCloseIsXButton && !hideCloseButton && (
                    <Button
                      hierarchy={'secondary'}
                      label={buttonCloseLabel ?? t`Close`}
                      onPress={() => {
                        void (!onCloseWithBtn ? close : onCloseWithBtn)()
                      }}
                    />
                  )}
                  {!props.customActionButton &&
                    (!!props.onAction || props.submitButton) && (
                      <Button
                        isDisabled={props.actionButtonDisabled}
                        type={props.submitButton ? 'submit' : 'button'}
                        hierarchy={'primary'}
                        isLoading={props.actionButtonShowLoader}
                        destructive={props.role === 'alertdialog'}
                        label={buttonActionLabel}
                        onPress={props.onAction}
                      />
                    )}
                  {props.customActionButton}
                </div>
              </div>
            )}
          </div>
        )}
      </RCADialog>
    )
  }
)

Dialog.displayName = 'Dialog'

export default Dialog
